/**
 * The time clock.
 *
 * "can we add a time clock in for record and payroll. So they log in and
 * clock in." — so it sits on the front desk, which is where everybody lands
 * after signing in, and it answers three things without being asked: whether
 * you're on, how long for, and who else is in.
 */
import { useCallback, useEffect, useState } from "react";
import { Clock, LogIn, LogOut, MessageSquarePlus } from "lucide-react";
import { Button, Input, Label, Modal, ModalFooter, Select, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export interface Shift {
  id: string;
  user_id: string;
  staff_name: string | null;
  credential: string | null;
  /** Their assigned colour — set on the staff record, used wherever they
   *  appear so the same person is never two different shades. */
  color?: string | null;
  started_at: string;
  ended_at: string | null;
  hours: number;
  open: boolean;
  note?: string | null;
  in_reason?: string | null;
  in_note?: string | null;
  out_reason?: string | null;
  out_note?: string | null;
  edited?: boolean;
}

/** Must match CLOCK_REASONS in api/src/routes/timeclock.ts. */
export const CLOCK_REASONS = [
  "Late",
  "Left early",
  "No internet",
  "Concierge visit",
  "Forgot to clock in",
  "Forgot to clock out",
  "Other",
] as const;

/** "3h 42m" — how people say it, not 3.7. */
export function hoursLabel(hours: number): string {
  const total = Math.max(0, Math.round(hours * 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * `compact` is the front desk's version: "Can we tuck the clock in, all the way
 * right under consults due, no need for that long thing." One short line beside
 * the button instead of a full-width card, so the clients stay the page.
 */
export function TimeClock({ compact = false }: { compact?: boolean } = {}) {
  const toast = useToast();
  const [on, setOn] = useState<boolean | null>(null);
  const [shift, setShift] = useState<Shift | null>(null);
  const [today, setToday] = useState(0);
  const [others, setOthers] = useState<Shift[]>([]);
  const [busy, setBusy] = useState(false);
  /* When their rota says they start today — null for anybody without one. */
  const [dueAt, setDueAt] = useState<string | null>(null);
  /**
   * "clocking in and out should have a box pop up if they are late they can
   *  write in a message like below"
   *
   * The press itself stays one press: nobody should have to fill a form in
   * to start work. The box opens straight afterwards, already knowing they
   * were twenty minutes late, and it can be closed without writing anything.
   */
  const [asking, setAsking] = useState<
    { shiftId: string; which: "in" | "out"; late: number | null } | null>(null);
  // Ticks the "on for" figure up while somebody is clocked in.
  const [, setNow] = useState(Date.now());

  const load = useCallback(() => {
    api.get<{ on: boolean; shift: Shift | null; hours_today: number; due_at: string | null }>("/staff/clock")
      .then((d) => { setOn(d.on); setShift(d.shift); setToday(d.hours_today); setDueAt(d.due_at ?? null); })
      .catch(() => { setOn(null); });
    api.get<{ on: Shift[] }>("/staff/clock/who")
      .then((d) => setOthers(d.on))
      .catch(() => setOthers([]));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const press = async (what: "in" | "out") => {
    setBusy(true);
    try {
      const d = await api.post<{ shift: Shift }>(`/staff/clock/${what}`, {});
      toast.success(what === "in" ? "Clocked in" : "Clocked out");
      /* Minutes past the rota, measured against the start we were handed -
         not the browser's idea of the hour. */
      const late = what === "in" && dueAt
        ? Math.round((new Date(d.shift.started_at).getTime() - new Date(dueAt).getTime()) / 60_000)
        : null;
      if (d.shift?.id && late !== null && late >= 5) {
        setAsking({ shiftId: d.shift.id, which: "in", late });
      }
      load();
    } catch (err) {
      // Already on, or already off — reload so the button tells the truth.
      toast.error(err instanceof ApiError ? err.message : "That didn't work");
      load();
    } finally { setBusy(false); }
  };

  /** The note link, for a shift that is running or has just ended. */
  const askAbout = (which: "in" | "out") => {
    if (!shift?.id) return;
    setAsking({ shiftId: shift.id, which, late: null });
  };

  const whyBox = asking ? (
    <WhyModal
      shiftId={asking.shiftId}
      which={asking.which}
      late={asking.late}
      onClose={() => setAsking(null)}
      onSaved={() => { setAsking(null); load(); }}
    />
  ) : null;

  if (on === null) return null;

  const onFor = shift ? (Date.now() - new Date(shift.started_at).getTime()) / 3_600_000 : 0;
  const alsoOn = others.filter((o) => o.id !== shift?.id);

  if (compact) {
    return (
      <div data-time-clock className={cn(
        "flex items-center gap-2 rounded-lg border px-2.5 py-1.5",
        on ? "border-emerald-600/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900/40",
      )}>
        <Clock className={cn("h-3.5 w-3.5 shrink-0", on ? "text-emerald-400" : "text-zinc-500")} />
        <span className="whitespace-nowrap text-[11px] text-zinc-400">
          {on ? `On since ${timeOf(shift!.started_at)}` : "Not on"}
          <span className="ml-1 text-zinc-600">{hoursLabel(today)} today</span>
        </span>
        <Button
          size="sm"
          variant={on ? "secondary" : "primary"}
          disabled={busy}
          leftIcon={on ? <LogOut className="h-3.5 w-3.5" /> : <LogIn className="h-3.5 w-3.5" />}
          onClick={() => void press(on ? "out" : "in")}
        >
          {on ? "Clock out" : "Clock in"}
        </Button>
        {on && shift && (
          <button
            type="button"
            onClick={() => askAbout("in")}
            title="Say why you clocked in when you did"
            aria-label="Add a note to this shift"
            className="shrink-0 rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </button>
        )}
        {whyBox}
      </div>
    );
  }

  return (
    <div data-time-clock className={cn(
      "rounded-xl border px-4 py-3",
      on ? "border-emerald-600/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900/40",
    )}>
      <div className="flex flex-wrap items-center gap-3">
        <Clock className={cn("h-4 w-4 shrink-0", on ? "text-emerald-400" : "text-zinc-500")} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-100">
            {on ? `On since ${timeOf(shift!.started_at)}` : "Not clocked in"}
          </p>
          <p className="text-[11px] text-zinc-500">
            {on && <>On for {hoursLabel(onFor)} · </>}
            {hoursLabel(today)} today
          </p>
        </div>
        <Button
          size="sm"
          variant={on ? "secondary" : "primary"}
          disabled={busy}
          leftIcon={on ? <LogOut className="h-3.5 w-3.5" /> : <LogIn className="h-3.5 w-3.5" />}
          onClick={() => void press(on ? "out" : "in")}
        >
          {on ? "Clock out" : "Clock in"}
        </Button>
      </div>
      {alsoOn.length > 0 && (
        <p className="mt-2 border-t border-zinc-800/70 pt-2 text-[11px] text-zinc-500">
          Also on:{" "}
          {alsoOn
            .map((o) => `${o.staff_name ?? "Someone"}${o.credential ? ` ${o.credential}` : ""} (since ${timeOf(o.started_at)})`)
            .join(", ")}
        </p>
      )}
      {on && shift && (
        <button
          type="button"
          onClick={() => askAbout("in")}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
          {shift.in_reason || shift.in_note ? "Change the note on this shift" : "Add a note to this shift"}
        </button>
      )}
      {whyBox}
    </div>
  );
}

/**
 * Why this clock-in is not when the rota says.
 *
 *   "Also clocking in and out should have a box pop up if they are late they
 *    can write in a message like below"
 *
 * A reason to pick and a line in their own words, exactly the pair she showed
 * me. Closing it without writing anything is allowed: the shift is already
 * recorded, this is only the explanation that goes beside it on the report.
 */
function WhyModal({ shiftId, which, late, onClose, onSaved }: {
  shiftId: string;
  which: "in" | "out";
  /** Minutes past the rota, when that is what opened this. */
  late: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState<string>(late !== null ? "Late" : "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/staff/shifts/${shiftId}/why`, {
        which, reason: reason || null, note: note.trim() || null,
      });
      toast.success("Saved on the shift");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={which === "in" ? "About this clock-in" : "About this clock-out"} size="sm">
      <div className="space-y-3">
        {late !== null && (
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
            That's {late} minute{late === 1 ? "" : "s"} past your start time. Anything you put here shows on the
            payroll report.
          </p>
        )}
        <div>
          <Label htmlFor="why-reason">Reason</Label>
          <Select id="why-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">— None —</option>
            {CLOCK_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        </div>
        <div>
          <Label htmlFor="why-note">In your own words</Label>
          <Input
            id="why-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. no internet at home, came straight from a concierge visit"
            maxLength={500}
          />
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Not now</Button>
        <Button loading={saving} disabled={!reason && !note.trim()} onClick={() => void save()}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
